import { useEffect, useMemo, useState } from "react";
import { Plus, Edit2, KeyRound, UserX, Loader2, Search, UserCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataPagination } from "@/components/ui/data-pagination";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import RelationalPicker from "@/admin/components/RelationalPicker";

const ROLES = [
  { v: "agent", label: "Agent" }, { v: "supervisor", label: "Supervisor" },
  { v: "admin", label: "Admin" }, { v: "super_admin", label: "Super Admin" },
];
const SUPER_SUPER_ROLE = { v: "super_super_admin", label: "Super Super Admin" };

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const roleOptions = useMemo(
    () => (currentUser?.role === "super_super_admin" ? [...ROLES, SUPER_SUPER_ROLE] : ROLES),
    [currentUser?.role],
  );
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("active"); // active | suspended
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [form, setForm] = useState({});
  const [resetPw, setResetPw] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [u, g] = await Promise.all([api.get("/admin/users"), api.get("/admin/groups")]);
      setUsers(u.data || []); setGroups(g.data || []);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startNew() {
    setForm({ name: "", email: "", role: "agent", password: "", agent_license: true, status: "active", group_ids: [], skills: [], skill_levels: {} });
    setEditing("new");
  }
  function startEdit(u) {
    setForm({ ...u, password: "", group_ids: u.group_ids || [], skills: u.skills || [], skill_levels: u.skill_levels || {} });
    setEditing(u);
  }
  async function save() {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.post("/admin/users", payload);
        toast.success("User created");
      } else {
        const { name, role, agent_license, group_ids, skills, skill_levels, status, avatar_color, presence } = form;
        await api.patch(`/admin/users/${editing.id}`, { name, role, agent_license, group_ids, skills, skill_levels, status, avatar_color, presence });
        toast.success("User updated");
      }
      setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function suspend(u) {
    if (!confirm(`Suspend ${u.name}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Suspended"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }
  async function doReset() {
    if (!resetPw || resetPw.length < 6) { toast.error("Min 6 chars"); return; }
    try {
      await api.post(`/admin/users/${resetUser.id}/reset-password`, { new_password: resetPw });
      toast.success("Password reset");
      setResetUser(null); setResetPw("");
    } catch (e) { toast.error(formatErr(e)); }
  }
  async function reactivate(u) {
    try { await api.patch(`/admin/users/${u.id}`, { status: "active" }); toast.success(`${u.name} reactivated`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }
  async function hardDelete(u) {
    if (!confirm(`Permanently delete ${u.name}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}?hard=true`); toast.success(`${u.name} deleted`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  const counts = useMemo(() => {
    let active = 0, suspended = 0;
    for (const u of users) (u.status === "suspended" ? suspended++ : active++);
    return { active, suspended };
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((u) => {
      const isSusp = u.status === "suspended";
      if (tab === "suspended" ? !isSusp : isSusp) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });
  }, [users, search, tab]);
  const PAGE_SIZE = 50;
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageClamped = Math.min(page, pages);
  const paged = useMemo(() => filtered.slice((pageClamped - 1) * PAGE_SIZE, pageClamped * PAGE_SIZE), [filtered, pageClamped]);
  useEffect(() => { setPage(1); }, [search, tab]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="admin-users-title">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">Invite, edit, assign groups & skills, reset passwords.</p>
        </div>
        <Button data-testid="add-user-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
          <Plus className="w-4 h-4" /> Add user
        </Button>
      </div>

      <div className="flex gap-1 border-b border-border mb-4" data-testid="users-tabs">
        {[{ k: "active", l: `Active (${counts.active})` }, { k: "suspended", l: `Suspended (${counts.suspended})` }].map((t) => (
          <button key={t.k} data-testid={`users-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="mb-4 relative max-w-sm">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="search-users-input" placeholder="Search users…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" />
      </div>

      <Card className="overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr className="text-left">
                  <th className="px-4 py-2 font-semibold">User</th>
                  <th className="px-4 py-2 font-semibold">Role</th>
                  <th className="px-4 py-2 font-semibold">Ext.</th>
                  <th className="px-4 py-2 font-semibold">Groups</th>
                  <th className="px-4 py-2 font-semibold">License</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((u) => (
                  <tr key={u.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-user-${u.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-7 w-7"><AvatarFallback style={{ background: u.avatar_color, color: "white" }} className="text-[11px] font-bold">{u.initials}</AvatarFallback></Avatar>
                        <div>
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5"><Badge variant="outline" className="font-mono text-[10px]">{u.role?.replace(/_/g, " ")}</Badge></td>
                    <td className="px-4 py-2.5">{u.extension ? <code className="text-xs font-mono">{u.extension}</code> : <span className="text-xs text-muted-foreground/60">—</span>}</td>
                    <td className="px-4 py-2.5 text-xs">{(u.group_ids||[]).map((gid) => groups.find((g) => g.id === gid)?.name).filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-4 py-2.5">{u.agent_license ? <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/15">Agent licensed</Badge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-2.5">{u.status === "suspended" ? <Badge variant="destructive">Suspended</Badge> : <Badge variant="outline">Active</Badge>}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        {u.status === "suspended" ? (
                          <>
                            <Button data-testid={`reactivate-user-${u.id}`} onClick={() => reactivate(u)} variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" title="Reactivate user"><UserCheck className="w-4 h-4" /></Button>
                            <Button data-testid={`delete-user-${u.id}`} onClick={() => hardDelete(u)} variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Delete permanently"><Trash2 className="w-4 h-4" /></Button>
                          </>
                        ) : (
                          <>
                            <Button data-testid={`edit-user-${u.id}`} onClick={() => startEdit(u)} variant="ghost" size="icon" className="h-8 w-8" title="Edit"><Edit2 className="w-4 h-4" /></Button>
                            <Button data-testid={`reset-pw-${u.id}`} onClick={() => setResetUser(u)} variant="ghost" size="icon" className="h-8 w-8" title="Reset password"><KeyRound className="w-4 h-4" /></Button>
                            <Button data-testid={`suspend-user-${u.id}`} onClick={() => suspend(u)} variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Suspend"><UserX className="w-4 h-4" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DataPagination page={pageClamped} pages={pages} total={filtered.length} pageSize={PAGE_SIZE} onPage={setPage} testId="users-pagination" />
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New user" : `Edit ${editing?.name}`}</DialogTitle>
            <DialogDescription>{editing === "new" ? "Sends invite with default password Connect7@2026 unless overridden." : "Update profile, role, license."}</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div><Label>Name</Label><Input data-testid="user-name-input" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
            <div><Label>Email</Label><Input data-testid="user-email-input" disabled={editing!=="new"} value={form.email||""} onChange={(e)=>setForm({...form, email:e.target.value})} /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v)=>setForm({...form, role:v})}>
                <SelectTrigger data-testid="user-role-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{roleOptions.map((r)=><SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editing === "new" && (
              <div><Label>Initial password</Label><Input data-testid="user-pw-input" type="password" placeholder="Connect7@2026" value={form.password||""} onChange={(e)=>setForm({...form, password:e.target.value})} /></div>
            )}
            <div className="flex items-center gap-2 sm:col-span-2">
              <Switch data-testid="user-license-switch" checked={!!form.agent_license} onCheckedChange={(v)=>setForm({...form, agent_license:!!v})} />
              <Label>Agent license (can take live calls)</Label>
            </div>
            <div className="sm:col-span-2">
              <Label>Groups</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {groups.map((g) => {
                  const on = (form.group_ids||[]).includes(g.id);
                  return (
                    <button key={g.id} data-testid={`user-group-pill-${g.id}`} type="button" onClick={() => {
                      const cur = form.group_ids || [];
                      setForm({ ...form, group_ids: on ? cur.filter((x)=>x!==g.id) : [...cur, g.id] });
                    }} className={`px-2.5 py-1 rounded-full text-xs border ${on?"bg-secondary text-secondary-foreground border-secondary":"bg-card hover:bg-muted border-border"}`}>
                      {g.name}
                    </button>
                  );
                })}
                {groups.length === 0 && <span className="text-xs text-muted-foreground">No groups yet — create some under "User Groups".</span>}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>Skills</Label>
              <RelationalPicker
                kind="skills" multi returnKey="name" testId="user-skills-picker"
                placeholder="Select skills from the catalog…"
                value={form.skills || []}
                onChange={(v) => {
                  const levels = { ...(form.skill_levels || {}) };
                  Object.keys(levels).forEach((k) => { if (!v.includes(k)) delete levels[k]; });
                  v.forEach((k) => { if (!(k in levels)) levels[k] = 3; });
                  setForm({ ...form, skills: v, skill_levels: levels });
                }}
              />
              {(form.skills || []).length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <p className="text-xs text-muted-foreground">Proficiency — 1 (beginner) to 5 (expert). Used to prefer experts in routing.</p>
                  {(form.skills || []).map((s) => (
                    <div key={s} className="flex items-center justify-between gap-3 bg-muted/30 rounded-md px-2.5 py-1.5">
                      <span className="text-sm font-medium">{s}</span>
                      <Select value={String((form.skill_levels || {})[s] || 3)} onValueChange={(lvl) => setForm({ ...form, skill_levels: { ...(form.skill_levels || {}), [s]: Number(lvl) } })}>
                        <SelectTrigger className="h-7 w-28" data-testid={`user-skill-level-${s}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>Level {n}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button>
            <Button data-testid="user-save-btn" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset PW dialog */}
      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset password — {resetUser?.name}</DialogTitle>
            <DialogDescription>The user will need this new password to sign in.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>New password (min 6 chars)</Label>
            <Input data-testid="reset-pw-input" type="password" value={resetPw} onChange={(e)=>setResetPw(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>{setResetUser(null);setResetPw("");}}>Cancel</Button>
            <Button data-testid="reset-pw-confirm-btn" onClick={doReset} className="bg-primary hover:bg-primary/90 text-primary-foreground">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
